import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNotification } from "../../context/notification-provider";
import FilterOptionForm, {
  FilterOptionFormState,
  FilterOptionFormValues,
  FormFieldValue,
  validateFilterOption,
} from "./filter-option-form";
import { createFilterOption } from "./service";

const FilterOptionCreateDialog = ({
  filterTypeId,
  open,
  onClose,
}: {
  filterTypeId: number;
  open: boolean;
  onClose: () => void;
}) => {
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [formState, setFormState] = React.useState<FilterOptionFormState>({
    values: {
      isEnabled: true,
      isDefaultSelected: false,
      isSynthetic: false,
      sortOrder: 0,
    },
    errors: {},
  });
  const formValues = formState.values;

  const handleFieldChange = React.useCallback(
    (name: keyof FilterOptionFormValues, value: FormFieldValue) => {
      setFormState((previousState) => ({
        ...previousState,
        values: { ...previousState.values, [name]: value },
      }));
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    const { issues } = validateFilterOption(formValues, false);

    if (issues.length > 0) {
      setFormState((previousState) => ({
        ...previousState,
        errors: Object.fromEntries(
          issues.map((issue) => [issue.path[0], issue.message]),
        ),
      }));
      return;
    }

    setFormState((previousState) => ({ ...previousState, errors: {} }));
    setIsSubmitting(true);

    try {
      await createFilterOption({
        filterTypeId,
        code: formValues.code ?? "",
        description: formValues.description ?? "",
        isEnabled: formValues.isEnabled ?? true,
        isDefaultSelected: formValues.isDefaultSelected ?? false,
        isSynthetic: formValues.isSynthetic ?? false,
        sortOrder: formValues.sortOrder ?? 0,
        meta: null,
      });

      showNotification({
        message: "Filter option created successfully.",
        severity: "success",
        duration: 3000,
      });

      onClose();
    } catch (createError) {
      showNotification({
        message: `Failed to create filter option. Reason: ${(createError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [filterTypeId, formValues, showNotification, onClose]);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Create Filter Option
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <FilterOptionForm
          formState={formState}
          isEdit={false}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Create filter option
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterOptionCreateDialog;
